import React, { useEffect, useMemo, useRef, useState } from 'react';

import { gsap } from 'gsap';
import CountUp from 'react-countup';
import { Trans, useTranslation } from 'react-i18next';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { getGlowLevel } from '@app/helpers';

import './PickerResultRhythmScore.scss';

interface PickerResultRhythmScoreProps {
    score: number;
    showScore: boolean;
    animationDuration?: number;
    onRhythmScoreClick?: (e) => void | null;
    preYat?: boolean;
}

export const PickerResultRhythmScore = ({
    score = 0,
    showScore = false,
    animationDuration = 1.25,
    onRhythmScoreClick,
    preYat = false,
}: PickerResultRhythmScoreProps) => {
    const { t } = useTranslation(['common', 'yat_picker']);
    const [startCount, setStartCount] = useState(0);
    const nodeRef = useRef(null);

    getGlowLevel(score);

    const variant = useMemo(() => {
        return getGlowLevel(score);
    }, [score]);

    const classNames = useMemo(() => {
        let classNames = 'picker-result-rhythm-score';
        if (variant) classNames += ` picker-result-rhythm-score--${variant}`;
        if (!showScore) classNames += ' picker-result-rhythm-score--mute-score';
        if (preYat) classNames += ' pre-yat';

        return classNames;
    }, [variant, showScore, preYat]);

    useEffect(() => {
        gsap.delayedCall(animationDuration, setStartCount, [score]);
    }, [score, animationDuration]);

    return (
        <div className={classNames}>
            <div className="picker-result-rhythm-score__score">
                <TransitionGroup>
                    <CSSTransition key={variant} timeout={1250} classNames="rhytm-score-circle" nodeRef={nodeRef}>
                        <div className={`picker-result-rhythm-score__circle -${variant}`} />
                    </CSSTransition>
                </TransitionGroup>
                <span className="picker-result-rhythm-score__number">
                    <CountUp start={startCount} end={score} duration={animationDuration} />
                </span>
                <button
                    className="picker-result-rhythm-score__mobile-help-button"
                    aria-label={t('yat_picker:tell_me_more')}
                    onClick={onRhythmScoreClick}
                >
                    <div className="picker-result-rhythm-score__mobile-help-button-inner v2-text-xxs">
                        <span aria-hidden="true">?</span>
                    </div>
                </button>
            </div>
            <div className="picker-result-rhythm-score__description">
                <span className="picker-result-rhythm-score__description-title v2-text-heading-5">
                    {t('rhythm_score')}
                </span>
                {!preYat && (
                    <div>
                        <p className="picker-result-rhythm-score__description-text v2-text-fluid-xs">
                            <Trans
                                i18nKey="rhythm_score_description"
                                ns="yat_picker"
                                components={{ strong: <strong /> }}
                            />
                        </p>
                        <button className="picker-result-rhythm-score__description-button" onClick={onRhythmScoreClick}>
                            {t('yat_picker:tell_me_more')}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
